<template>
  <div ref="slider" class="custom-slider minmax relative bg-grey-900">
    <input
      id="min"
      ref="inputMin"
      type="range"
      :min="min"
      :max="max"
      :value="localMinValue"
      :step="step"
      @input="onInputMin"
    />
    <input
      id="max"
      ref="inputMax"
      type="range"
      name="max"
      :min="min"
      :max="max"
      :value="localMaxValue"
      :step="step"
      @input="onInputMax"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

const props = withDefaults(
  defineProps<{
    min?: number
    max?: number
    gap?: number
    minValue: number
    maxValue: number
    step?: number
  }>(),
  {
    min: 0,
    max: 100,
    gap: 1,
    step: 1,
  },
)

const emit = defineEmits(['changeMinValue', 'changeMaxValue'])
const slider = ref<HTMLDivElement | null>(null)
const inputMin = ref<HTMLInputElement | null>(null)
const inputMax = ref<HTMLInputElement | null>(null)

const localMinValue = computed({
  get: () => props.minValue,
  set: (value: number) => emit('changeMinValue', value),
})

const localMaxValue = computed({
  get: () => props.maxValue,
  set: (value: number) => emit('changeMaxValue', value),
})

function onInputMin(event: Event) {
  const target = event.target as HTMLInputElement
  const value = parseFloat(target.value)

  if (value > localMaxValue.value - props.gap) {
    target.value = (localMaxValue.value - props.gap).toString()
    return
  }

  localMinValue.value = value
}

function onInputMax(event: Event) {
  const target = event.target as HTMLInputElement
  const value = parseFloat(target.value)

  if (value < localMinValue.value + props.gap) {
    target.value = (localMinValue.value + props.gap).toString()
    return
  }

  localMaxValue.value = value
}
</script>

<style scoped>
.slider-cont {
  width: 300px;
}

.custom-slider {
  --track-height: 2px;
  --thumb-radius: 20px;
}

/* style the input element with type "range" */
input[type='range'] {
  position: absolute;
  width: calc(100% - var(--thumb-radius));
  appearance: none;
  background: none;
  border-radius: 100%;
  z-index: 0;
  height: 100%;
  pointer-events: none;
}

/* `::-webkit-slider-runnable-track` targets the track (background) of a range slider in chrome and safari browsers. */
input[type='range']::-webkit-slider-runnable-track {
  appearance: none;
  height: var(--track-height);
}

input[type='range']::-webkit-slider-thumb {
  @apply bg-p-500 pointer-events-auto cursor-pointer appearance-none rounded-full;

  position: relative;
  width: var(--thumb-radius);
  height: var(--thumb-radius);
  margin-top: calc((var(--track-height) - var(--thumb-radius)) / 2);
  z-index: 1;
}

.custom-slider.minmax {
  @apply rounded-full;

  height: var(--track-height);
  margin: 0.5rem 0;

  --progress-left: 0%;
  --progress-right: 0%;
}

input[type='range'][name='max'] {
  left: var(--thumb-radius);
}
</style>
